import { DataTable, EmptyState } from '@aidkitorg/component-library';
import { ColumnDef } from '@tanstack/react-table';
import { DashboardSummaryTable, DashboardComponentResult } from '@aidkitorg/types/lib/admin-panel/dashboard';
import { useMemo } from 'react';
import { useLocalizedStrings } from '../../../../Localization';

type SummaryTableProps = {
  component: DashboardSummaryTable;
  result: DashboardComponentResult | undefined;
  height: number;
}

export const SummaryTable = ({ component, result, height }: SummaryTableProps) => {
  const L = useLocalizedStrings();
  const showPercentages = component.showPercentages === true;

  const { columns, data } = useMemo(() => {
    if (!result?.data || !Array.isArray(result.data) || result.data.length === 0) {
      return { columns: [], data: [] };
    }

    // Get all unique keys from the data to create columns
    const allKeys = new Set<string>();
    result.data.forEach((row: Record<string, any>) => {
      Object.keys(row).forEach(key => allKeys.add(key));
    });

    // Use totalCount from result for percentage calculation
    const total = result.totalCount || 0;

    // Create columns based on the component configuration
    const columns: ColumnDef<Record<string, any>>[] = [];

    // Add group columns first (these are the fields we're grouping by)
    component.groups.forEach((group) => {
      if (group.kind === 'Field') {
        // Handle synthetic UID translation - backend converts __synthetic_uid__ to uid
        const actualFieldName = group.field === '__synthetic_uid__' ? 'uid' : group.field;

        if (allKeys.has(actualFieldName)) {
          columns.push({
            id: actualFieldName,
            header: formatColumnHeader(group.field), // Use original field name for header
            accessorKey: actualFieldName, // Use actual field name for data access
            cell: ({ getValue }) => {
              const value = getValue();
              return value != null ? String(value) : '';
            }
          });
        }
      }
    });

    // Add summary columns (typically the count or aggregated values)
    component.summary.forEach((summaryExpr, index) => {
      // For Count expressions, look for the summary key from backend
      if (summaryExpr.kind === 'Count') {
        const summaryKey = result.summaryKey || 'count';
        if (allKeys.has(summaryKey)) {
          columns.push({
            id: summaryKey,
            header: 'Count',
            accessorKey: summaryKey,
            cell: ({ getValue }) => {
              const value = getValue();
              const numValue = Number(value);
              if (isNaN(numValue)) return String(value);
              const formattedValue = numValue.toLocaleString();
              if (!showPercentages || !total) return formattedValue;
              const percentage = ((numValue / total) * 100).toFixed(1);
              return (
                <span>
                  {formattedValue}
                  <span className="ml-2 text-gray-500">({percentage}%)</span>
                </span>
              );
            }
          });
        }
      } else {
        // For other summary types (Sum, Mean, etc.), use the summary key
        const summaryKey = result.summaryKey || `summary_${index}`;
        if (allKeys.has(summaryKey)) {
          const headerName = summaryExpr.kind === 'Sum' ? 'Sum' :
            summaryExpr.kind === 'Mean' ? 'Average' : 'Value';
          columns.push({
            id: summaryKey,
            header: headerName,
            accessorKey: summaryKey,
            cell: ({ getValue }) => {
              const value = getValue();
              const numValue = Number(value);
              if (isNaN(numValue)) return String(value);
              const formattedValue = numValue.toLocaleString();
              if (!showPercentages || !total) return formattedValue;
              const percentage = ((numValue / total) * 100).toFixed(1);
              return (
                <span>
                  {formattedValue}
                  <span className="ml-2 text-gray-500">({percentage}%)</span>
                </span>
              );
            }
          });
        }
      }
    });

    return {
      columns,
      data: result.data as Record<string, any>[]
    };
  }, [component, result, showPercentages]);

  // Helper function to format column headers
  function formatColumnHeader(field: string): string {
    // Handle special cases first
    if (field === '__synthetic_uid__') {
      return L.admin_panel.reports.applicant_uid;
    }
    
    // Convert snake_case or camelCase to Title Case
    return field
      .replace(/([a-z])([A-Z])/g, '$1 $2') // camelCase to spaces
      .replace(/_/g, ' ') // snake_case to spaces
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  if (!result?.data || data.length === 0) {
    return (
      <EmptyState variant="widget" />
    );
  }

  return (
    <DataTable
      className="p-2 w-full"
      columns={columns}
      data={data}
      compact
      noDataMessage="No summary data available"
      initialSorting={[]}
      showPagination={true}
      stickyHeader
      tableHeight={height}
      pageSize={10}
      verticalAlign="middle"
      alternateRowShading
    />
  );
};
